import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import Layout from '../components/Layout';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const fmtEur = (n: number) => new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(n || 0);

export const SettlementConfigPage: React.FC = () => {
  const navigate = useNavigate();
  const { accessToken, isAuthenticated } = useAuthStore();
  const headers = { Authorization: `Bearer ${accessToken}` };

  const [org, setOrg] = useState<any>(null);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [sigImgV, setSigImgV] = useState(0);   // cache-buster voor de handtekening-afbeelding

  const load = useCallback(async () => {
    try {
      const o = await fetch(`${API_BASE_URL}/api/admin/organisatie`, { headers }).then((x) => (x.ok ? x.json() : null));
      setOrg(o);
    } catch { setError('Laden mislukt'); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  useEffect(() => {
    if (!isAuthenticated || !accessToken) { navigate('/login'); return; }
    load();
  }, [isAuthenticated, accessToken, navigate, load]);

  const flash = (m: string) => { setToast(m); setError(''); setTimeout(() => setToast(''), 3500); };
  const saveOrg = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/organisatie`, {
        method: 'PUT', headers: { ...headers, 'Content-Type': 'application/json' }, body: JSON.stringify(org),
      });
      if (!res.ok) throw new Error('Opslaan mislukt');
      flash('Organisatiegegevens opgeslagen');
    } catch (err) { setError(err instanceof Error ? err.message : 'Mislukt'); }
  };
  const uploadSigImg = async (file: File | null) => {
    if (!file) return;
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await fetch(`${API_BASE_URL}/api/admin/organisatie/handtekening-afbeelding`, { method: 'POST', headers, body: fd });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Upload mislukt'); }
      setOrg((o: any) => ({ ...o, heeft_handtekening_afbeelding: true }));
      setSigImgV((v) => v + 1);
      flash('Handtekening-afbeelding opgeslagen');
    } catch (err) { setError(err instanceof Error ? err.message : 'Mislukt'); }
  };
  const verwijderSigImg = async () => {
    await fetch(`${API_BASE_URL}/api/admin/organisatie/handtekening-afbeelding`, { method: 'DELETE', headers });
    setOrg((o: any) => ({ ...o, heeft_handtekening_afbeelding: false }));
    setSigImgV((v) => v + 1);
    flash('Afbeelding verwijderd');
  };

  const inp = "w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-mfb-blue outline-none";

  return (
    <Layout>
      <div className="mb-6">
        <h1 className="font-display text-2xl font-semibold text-mfb-blue">Afrekeninstellingen</h1>
        <p className="text-sm text-slate-500">Premieplan en belastingtarieven — versiebeheerd, live gebruikt door de afreken-engine. De verdeelsleutel staat onder Financieel.</p>
      </div>
      {toast && <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-800">{toast}</div>}
      {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>}

      {/* ORGANISATIEGEGEVENS */}
      <h2 className="font-display text-lg font-semibold text-mfb-blue mb-3">Organisatiegegevens (afzender op documenten)</h2>
      {org && (
        <form onSubmit={saveOrg} className="bg-white rounded-2xl border border-slate-200 p-6 mb-10">
          <p className="text-sm text-slate-500 mb-4">
            Deze stamdata van MijnFactuurVerzekerd verschijnt op facturen en schadestaten, met een verwijzing naar verzekeraar {org.verzekeraar || 'Allianz Trade'}.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              ['handelsnaam', 'Handelsnaam'], ['juridische_naam', 'Juridische naam'], ['kvk', 'KvK-nummer'],
              ['btw', 'BTW-nummer'], ['adres', 'Adres'], ['postcode', 'Postcode'],
              ['plaats', 'Plaats'], ['iban', 'IBAN'], ['email', 'E-mail'],
              ['telefoon', 'Telefoon'], ['website', 'Website'], ['verzekeraar', 'Verzekeraar'],
            ].map(([key, label]) => (
              <div key={key}>
                <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
                <input value={org[key] || ''} onChange={(e) => setOrg({ ...org, [key]: e.target.value })} className={inp} />
              </div>
            ))}
          </div>
          <div className="mt-4">
            <label className="block text-xs font-medium text-slate-600 mb-1">Verzekeraar-notitie (op document)</label>
            <input value={org.verzekeraar_notitie || ''} onChange={(e) => setOrg({ ...org, verzekeraar_notitie: e.target.value })} className={inp} />
          </div>

          <div className="mt-6 pt-5 border-t border-slate-100">
            <h3 className="font-semibold text-mfb-blue mb-1">Kredietchecks bij Allianz Trade</h3>
            <p className="text-xs text-slate-500 mb-3">Eén contractnummer voor de afrekening van kredietchecks (grades) over álle collectieven, en het adres waar registratieverzoeken voor nieuwe debiteuren naartoe gaan.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Kredietcheck-contractnummer</label>
                <input value={org.kredietcheck_contractnummer || ''} onChange={(e) => setOrg({ ...org, kredietcheck_contractnummer: e.target.value })} className={inp} />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Allianz-aanmeldadres (e-mail nieuwe debiteuren)</label>
                <input type="email" value={org.allianz_aanmeld_email || ''} onChange={(e) => setOrg({ ...org, allianz_aanmeld_email: e.target.value })} placeholder="registratie@allianz-trade.nl" className={inp} />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Allianz-incassoadres (e-mail doorgezette incasso's)</label>
                <input type="email" value={org.allianz_incasso_email || ''} onChange={(e) => setOrg({ ...org, allianz_incasso_email: e.target.value })} placeholder="incasso@allianz-trade.nl" className={inp} />
              </div>
            </div>
          </div>

          <div className="mt-6 pt-5 border-t border-slate-100">
            <h3 className="font-semibold text-mfb-blue mb-1">Ondertekening e-mail & mailbox</h3>
            <p className="text-xs text-slate-500 mb-3">Wordt automatisch onder elk uitgaand bericht/e-mail van de backoffice gezet.</p>

            <label className="block text-xs font-medium text-slate-600 mb-1">Handtekening met opmaak (e-mail)</label>
            <RichSignatureEditor value={org.mailbox_handtekening_html || ''} onChange={(html) => setOrg({ ...org, mailbox_handtekening_html: html })} />
            <p className="text-[11px] text-slate-400 mt-1">Opmaak (vet, cursief, links) wordt in de HTML-e-mail toegepast. De afbeelding hieronder verschijnt eronder.</p>

            <div className="mt-4">
              <label className="block text-xs font-medium text-slate-600 mb-1">Handtekening- of logo-afbeelding</label>
              <div className="flex items-center gap-3">
                {org.heeft_handtekening_afbeelding && <SigImagePreview token={accessToken!} version={sigImgV} />}
                <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-50">
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => { uploadSigImg(e.target.files?.[0] || null); e.target.value = ''; }} />
                  {org.heeft_handtekening_afbeelding ? 'Vervangen' : '+ Afbeelding kiezen'}
                </label>
                {org.heeft_handtekening_afbeelding && (
                  <button type="button" onClick={verwijderSigImg} className="text-xs text-slate-400 hover:text-red-600">verwijderen</button>
                )}
              </div>
              <p className="text-[11px] text-slate-400 mt-1">PNG/JPG, max 2 MB. Wordt inline meegestuurd in de e-mail (cid:handtekening).</p>
            </div>

            <details className="mt-4">
              <summary className="text-xs font-medium text-slate-500 cursor-pointer">Platte-tekst fallback (portal &amp; tekst-e-mail)</summary>
              <textarea value={org.mailbox_handtekening || ''} onChange={(e) => setOrg({ ...org, mailbox_handtekening: e.target.value })}
                rows={3} placeholder={'Met vriendelijke groet,\nTeam MijnFactuurVerzekerd'} className={`${inp} mt-2`} />
            </details>
          </div>

          <button className="mt-5 px-4 py-2.5 bg-mfb-orange hover:bg-mfb-orange-dark text-white font-semibold rounded-lg transition">Opslaan</button>
        </form>
      )}

      {/* Pakketconfiguratie per collectief (samengevat) */}
      <div className="mb-10 rounded-2xl border border-slate-200 bg-slate-50 p-5">
        <h2 className="font-display text-lg font-semibold text-mfb-blue mb-1">Pakket &amp; tarieven per collectief</h2>
        <p className="text-sm text-slate-600">
          De volledige pakketconfiguratie stel je per collectief in: het abonnement (premie + servicefee), de verzekeringsvoorwaarden,
          de dekkingstabel per grade, het polisnummer en de tarieven buiten de bundel (kredietcheck, incasso, verzekering). Beheer dit
          bij het aanmaken van een collectief of via{' '}
          <button onClick={() => navigate('/backoffice/collectivities')} className="font-semibold text-mfb-orange hover:underline">Collectiviteiten → Instellingen</button>.
        </p>
      </div>

      {/* KREDIETADVIES (kredietopinies-pakket, 1 tabel over alle collectieven) */}
      <h2 className="font-display text-lg font-semibold text-mfb-blue mb-1">Kredietadvies per grade (kredietopinies-pakket)</h2>
      <p className="text-sm text-slate-500 mb-3">Eén tabel over álle collectieven. Een advies kan hoger zijn dan het maximaal verzekerde bedrag.</p>
      <GradeAdviesSection headers={headers} />

      <DocumentsSection />
    </Layout>
  );
};

// Eenvoudige rich-text editor (contentEditable) voor de e-mailhandtekening
const RichSignatureEditor: React.FC<{ value: string; onChange: (html: string) => void }> = ({ value, onChange }) => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (value || '')) ref.current.innerHTML = value || '';
    // alleen initialiseren
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const cmd = (c: string, v?: string) => {
    document.execCommand(c, false, v);
    ref.current?.focus();
    onChange(ref.current?.innerHTML || '');
  };
  const Btn: React.FC<{ onClick: () => void; children: React.ReactNode; title?: string }> = ({ onClick, children, title }) => (
    <button type="button" title={title} onMouseDown={(e) => { e.preventDefault(); onClick(); }}
      className="px-2 py-1 text-sm rounded border border-slate-200 text-slate-600 hover:bg-slate-50 min-w-[30px]">{children}</button>
  );
  return (
    <div>
      <div className="flex flex-wrap gap-1 mb-1">
        <Btn onClick={() => cmd('bold')} title="Vet"><b>B</b></Btn>
        <Btn onClick={() => cmd('italic')} title="Cursief"><i>I</i></Btn>
        <Btn onClick={() => cmd('underline')} title="Onderstrepen"><u>U</u></Btn>
        <Btn onClick={() => { const u = window.prompt('Link-URL (incl. https://):'); if (u) cmd('createLink', u); }} title="Link">🔗</Btn>
        <Btn onClick={() => cmd('insertUnorderedList')} title="Lijst">•</Btn>
        <Btn onClick={() => cmd('removeFormat')} title="Opmaak wissen">✕</Btn>
      </div>
      <div ref={ref} contentEditable suppressContentEditableWarning
        onInput={() => onChange(ref.current?.innerHTML || '')}
        className="min-h-[110px] w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-mfb-blue outline-none text-sm leading-relaxed" />
    </div>
  );
};

// Voorbeeld van de handtekening-afbeelding (met auth → blob → object URL)
const SigImagePreview: React.FC<{ token: string; version: number }> = ({ token, version }) => {
  const [url, setUrl] = useState('');
  useEffect(() => {
    let obj = '';
    fetch(`${API_BASE_URL}/api/admin/organisatie/handtekening-afbeelding`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.blob() : null))
      .then((b) => { if (b) { obj = URL.createObjectURL(b); setUrl(obj); } });
    return () => { if (obj) URL.revokeObjectURL(obj); };
  }, [token, version]);
  if (!url) return null;
  return <img src={url} alt="handtekening" className="max-h-16 rounded border border-slate-200 bg-white p-1" />;
};

const GradeAdviesSection: React.FC<{ headers: any }> = ({ headers }) => {
  const [rows, setRows] = useState<any[]>([]);
  const [g, setG] = useState('1'); const [bedrag, setBedrag] = useState(''); const [tekst, setTekst] = useState('');
  const [msg, setMsg] = useState('');
  const laad = useCallback(async () => {
    const r = await fetch(`${API_BASE_URL}/api/settlement/grade-advies`, { headers });
    if (r.ok) setRows(await r.json());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { laad(); }, [laad]);
  const save = async () => {
    const r = await fetch(`${API_BASE_URL}/api/settlement/grade-advies`, {
      method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ grade: +g, advies_bedrag: +bedrag, advies_tekst: tekst || null }),
    });
    if (r.ok) { setBedrag(''); setTekst(''); setMsg(`Advies grade ${g} opgeslagen`); setTimeout(() => setMsg(''), 2500); laad(); }
  };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
      <div className="bg-white rounded-2xl border border-slate-200 p-6 h-fit space-y-3">
        <h3 className="font-semibold text-mfb-blue">Kredietadvies instellen</h3>
        <div><label className="block text-sm font-medium text-slate-700 mb-1">Grade</label>
          <select value={g} onChange={(e) => setG(e.target.value)} className="w-full px-2 py-2 border border-slate-300 rounded-lg bg-white">
            {[1,2,3,4,5,6,7,8,9,10].map((x) => <option key={x} value={x}>{x}</option>)}
          </select></div>
        <div><label className="block text-sm font-medium text-slate-700 mb-1">Geadviseerde limiet (€)</label>
          <input type="number" value={bedrag} onChange={(e) => setBedrag(e.target.value)} className="w-full px-2 py-2 border border-slate-300 rounded-lg" /></div>
        <div><label className="block text-sm font-medium text-slate-700 mb-1">Toelichting</label>
          <input value={tekst} onChange={(e) => setTekst(e.target.value)} className="w-full px-2 py-2 border border-slate-300 rounded-lg" /></div>
        <button onClick={save} className="w-full py-2.5 bg-mfb-orange hover:bg-mfb-orange-dark text-white font-semibold rounded-lg transition">Opslaan</button>
        {msg && <p className="text-xs text-emerald-700">{msg}</p>}
      </div>
      <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto"><table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50"><tr>
            <th className="px-5 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Grade</th>
            <th className="px-5 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Geadviseerde limiet</th>
            <th className="px-5 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Toelichting</th>
          </tr></thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r) => (
              <tr key={r.grade} className="hover:bg-slate-50">
                <td className="px-5 py-2 font-medium text-mfb-blue">{r.grade}</td>
                <td className="px-5 py-2 text-slate-700">{r.advies_bedrag > 0 ? fmtEur(r.advies_bedrag) : 'Geen krediet'}</td>
                <td className="px-5 py-2 text-slate-500">{r.advies_tekst || '—'}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={3} className="px-5 py-4 text-slate-400">Nog niet ingesteld.</td></tr>}
          </tbody>
        </table></div>
      </div>
    </div>
  );
};

const DOC_LABEL: Record<string, string> = {
  algemene_voorwaarden: 'Algemene voorwaarden',
  verzekeringsvoorwaarden: 'Verzekeringsvoorwaarden',
  privacyverklaring: 'Privacyverklaring',
};

const DocumentsSection: React.FC = () => {
  const { accessToken } = useAuthStore();
  const headers = { Authorization: `Bearer ${accessToken}` };
  const [docs, setDocs] = useState<any[]>([]);
  const [toast, setToast] = useState('');

  const load = useCallback(async () => {
    const r = await fetch(`${API_BASE_URL}/api/admin/documents`, { headers });
    if (r.ok) setDocs(await r.json());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);
  useEffect(() => { load(); }, [load]);

  const set = (soort: string, patch: any) => setDocs((ds) => ds.map((d) => (d.soort === soort ? { ...d, ...patch } : d)));
  const save = async () => {
    const r = await fetch(`${API_BASE_URL}/api/admin/documents`, {
      method: 'PUT', headers: { ...headers, 'Content-Type': 'application/json' }, body: JSON.stringify(docs),
    });
    if (r.ok) { setToast('Voorwaarden gepubliceerd naar de portals'); setTimeout(() => setToast(''), 3000); load(); }
  };

  return (
    <div className="mt-8">
      <h2 className="font-display text-lg font-semibold text-mfb-blue mb-1">Voorwaarden &amp; verklaringen</h2>
      <p className="text-sm text-slate-500 mb-3">Zet een link en vink "Zichtbaar" aan om het document in de leden-portal te tonen.</p>
      {toast && <div className="mb-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-800">{toast}</div>}
      <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
        {docs.map((d) => (
          <div key={d.soort} className="grid md:grid-cols-12 gap-3 items-end border-b border-slate-100 pb-4 last:border-0 last:pb-0">
            <div className="md:col-span-3">
              <div className="text-sm font-medium text-mfb-blue">{DOC_LABEL[d.soort] || d.titel}</div>
              <div className="text-xs text-slate-400">{d.uitgever}</div>
            </div>
            <div className="md:col-span-7">
              <label className="block text-xs font-medium text-slate-600 mb-1">Link (URL of /voorwaarden/bestand.pdf)</label>
              <input value={d.url || ''} onChange={(e) => set(d.soort, { url: e.target.value })} placeholder="https://… of /voorwaarden/…"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-mfb-blue outline-none text-sm" />
            </div>
            <div className="md:col-span-2">
              <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={!!d.zichtbaar} onChange={(e) => set(d.soort, { zichtbaar: e.target.checked })} /> Zichtbaar
              </label>
            </div>
          </div>
        ))}
        <button onClick={save} className="px-4 py-2 bg-mfb-orange hover:bg-mfb-orange-dark text-white font-semibold rounded-lg transition">
          Publiceren naar portals
        </button>
      </div>
    </div>
  );
};

export default SettlementConfigPage;
